import { useMemo } from 'react';
import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  SimpleGrid,
  Text,
} from '@mantine/core';
import {
  IconDots,
  IconEdit,
  IconTrash,
  IconPlayerStop,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient } from '@medplum/fhirtypes';
import { TherapeuticServiceFormData } from './TherapeuticServiceModal';
import { calculateAgeAtGivenTime } from '@/utils/calculate-age-at-given-time';
import { useDateDisplay } from '@hooks/useDateDisplay';

export type TherapeuticServiceAction = 'edit' | 'stop' | 'delete';

export interface TherapeuticServiceListItemProps {
  service: TherapeuticServiceFormData;
  patient: Patient;
  onAction?: (action: TherapeuticServiceAction) => void;
  isDeleting?: boolean;
  hideActions?: boolean;
}

// Helper component for labeled fields
const LabeledField = ({
  label,
  value,
}: {
  label: string;
  value: string | undefined | null;
}) => {
  if (!value) {
    return null;
  }
  return (
    <Text size="xs" c="dimmed">
      <Text component="span" fw={500} c="dark">
        {label}:
      </Text>{' '}
      {value}
    </Text>
  );
};

export const TherapeuticServiceListItem = ({
  service,
  patient,
  onAction,
  isDeleting = false,
  hideActions = false,
}: TherapeuticServiceListItemProps) => {
  const { t } = useTranslation(['patient-pages']);
  const formatDate = useDateDisplay({
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  // Extract service name
  const serviceName =
    service.service?.text ||
    service.service?.coding?.[0]?.display ||
    t('therapeuticServices.unknownService', {
      defaultValue: 'Unknown service',
    });

  // Calculate age at start date - show years + months format
  const startAgeText = useMemo(() => {
    if (!service.startDate || !patient.birthDate) {
      return undefined;
    }

    try {
      const birthDate = new Date(patient.birthDate);
      const age = calculateAgeAtGivenTime(birthDate, service.startDate);

      // Format: "5 years, 3 months" (omit months if 0)
      if (age.months === 0) {
        return t('therapeuticServices.startedAtAge', {
          defaultValue: '{{years}} years',
          years: age.years,
        });
      }
      return t('therapeuticServices.startedAtAgeWithMonths', {
        defaultValue: '{{years}} years, {{months}} months',
        years: age.years,
        months: age.months,
      });
    } catch {
      return undefined;
    }
  }, [service.startDate, patient.birthDate, t]);

  // Format dates
  const startDateText = formatDate(service.startDate);
  const endDateText = formatDate(service.terminationDate);

  // Check if service is stopped
  const serviceIsStopped = service.status === 'stopped';

  // Check if we can edit/stop/delete (only saved services have IDs)
  const canEdit = Boolean(service.id) && !serviceIsStopped && !isDeleting;
  const canStop = Boolean(service.id) && !serviceIsStopped && !isDeleting;
  const canDelete = Boolean(service.id) && !isDeleting;
  const hasActions = !hideActions && (canEdit || canStop || canDelete);

  return (
    <Paper p="sm" withBorder radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box flex={1}>
          {/* Service name */}
          <Text fw={600} size="sm" mb={8}>
            {serviceName}
          </Text>

          {/* Date fields and age in 2-column grid */}
          <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
            <LabeledField
              label={t('therapeuticServices.onsetDate', {
                defaultValue: 'Onset Date',
              })}
              value={startDateText}
            />
            <LabeledField
              label={t('therapeuticServices.terminationDate', {
                defaultValue: 'Termination Date',
              })}
              value={endDateText}
            />
            {startAgeText && (
              <Text size="xs" c="dimmed">
                <Text component="span" fw={500} c="dark">
                  {t('therapeuticServices.ageAtStart', {
                    defaultValue: 'Age at Start',
                  })}
                  :
                </Text>{' '}
                {startAgeText}
              </Text>
            )}
          </SimpleGrid>

          {/* Full-width fields */}
          {service.response && (
            <Box mt={4}>
              <LabeledField
                label={t('therapeuticServices.responseLabel', {
                  defaultValue: 'Response',
                })}
                value={service.response}
              />
            </Box>
          )}
        </Box>

        {/* Actions menu or loading indicator */}
        {isDeleting ? (
          <Loader size="sm" />
        ) : (
          hasActions && (
            <Menu shadow="md" width={160} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                {canEdit && (
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={() => onAction?.('edit')}
                  >
                    {t('therapeuticServices.actions.edit', {
                      defaultValue: 'Edit',
                    })}
                  </Menu.Item>
                )}
                {canStop && (
                  <Menu.Item
                    leftSection={<IconPlayerStop size={14} />}
                    onClick={() => onAction?.('stop')}
                    color="red"
                  >
                    {t('therapeuticServices.actions.stop', {
                      defaultValue: 'Stop',
                    })}
                  </Menu.Item>
                )}
                {canDelete && (
                  <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onAction?.('delete')}
                    color="red"
                  >
                    {t('therapeuticServices.actions.delete', {
                      defaultValue: 'Delete',
                    })}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )
        )}
      </Group>
    </Paper>
  );
};
